import {yupResolver} from '@hookform/resolvers/yup';
import {Autocomplete, Box, FormControl, Grid, InputLabel, ListSubheader, MenuItem, Select, TextField} from '@mui/material';
import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {useRecoilRefresher_UNSTABLE, useRecoilValue, useSetRecoilState} from 'recoil';
import colors from '../../../assets/theme/base/colors';
import {EEScorecardService} from '../../../Component/Api/EmploymentEquity/Scorecard/EEScorecardService';
import {MDButton} from '../../../Component/Common/Components/MDButton';
import {MDTypography} from '../../../Component/Common/Components/MDTypography';
import {EEScorecard} from '../../../Component/Model/Dto/EmploymentEquity/EEScorecard';
import {defaultObject, emptyGuid} from '../../../Helpers/typescriptHelper';
import {allScorecardState} from '../../../State/ScorecardSelection.Atom';
import {LinearProgressWithLabel} from '../GeneralInformationPage/GeneralInformation';
import {consolidationSchema} from './ConsolidationValidationSchema';
import {LoadingButton} from '@mui/lab';
import {useHistory} from "react-router-dom";
import {ConfirmationDialog} from "../../../Component/Common/Components/ConfirmationDialog";
import {OccupationalLevelTypes} from '../../../Component/Model/Enum/BEE123Enums';
import {ScorecardAtom} from '../../../State/Scorecard.Atom';
import {Reconsolidation} from './Reconsolidation';
import {useSignalRConnection} from "../../../Hooks/useSignalRConnection";

const {dark} = colors;

interface ISelectedScorecard {
    id: string;
    scorecardName: string;
    group?: string;
}

interface IProps {
    metaData: EEScorecard.MetaDto;
    selectedScorecards: ISelectedScorecard[];
    previous: () => void
}

export const ConsolidationRules = (props : IProps) => {
    const history = useHistory();
    const { formState: { errors } } = useForm({
        mode: 'all',
        resolver: yupResolver(consolidationSchema)
    });

    const generalInformation: ISelectedScorecard[] = [...props.selectedScorecards];
    
    const [selectedScorecardWithMap, setSelectedScorecardWithMap] = React.useState<NonNullable<
        {   scorecard: ISelectedScorecard;
            execSelection: EEScorecard.OccupationalTargetDto|undefined;
            otherExecSelection: EEScorecard.OccupationalTargetDto|undefined;
            seniorSelection: EEScorecard.OccupationalTargetDto|undefined;
            middleSelection: EEScorecard.OccupationalTargetDto|undefined;
            juniorSelection: EEScorecard.OccupationalTargetDto|undefined;
        }>[]>([]);
    
    const [execSelection, setExecSelection] = React.useState(defaultObject);
    const [otherExecSelection, setOtherExecSelection] = React.useState(defaultObject);
    const [seniorSelection, setSeniorSelection] = React.useState(defaultObject);
    const [middleSelection, setMiddleSelection] = React.useState(defaultObject);
    const [juniorSelection, setJuniorSelection] = React.useState(defaultObject);
    const [employmentEquityPlanSources, setEmploymentEquityPlanSources] = React.useState<ISelectedScorecard[]>([]);
    const [generalInformationSelection, setGeneralInformationSelection] = React.useState('0');
    const [objectivesSelection, setObjectivesSelection] = React.useState<string[]>([]);
    const [barriersSelection, setBarriersSelection] = React.useState<string[]>([]);
    const [workforceCommentsSelection, setWorkforceCommentsSelection] = React.useState<string[]>([]);
    const [workforceProjectedHeadcountSelection, setWorkforceProjectedHeadcountSelection] = React.useState<string[]>([]);
    const [workforceYearlyTargetsSelection, setWorkforceYearlyTargetsSelection] = React.useState<string[]>([]);
    const [additionalMonitoringProgressSelection, setAdditionalMonitoringProgressSelection] = React.useState<string[]>([]);
    const [additionalDisputeResolutionsSelection, setAdditionalDisputeResolutionsSelection] = React.useState<string[]>([]);
    const [additionalSeniorManagersSelection, setAdditionalSeniorManagersSelection] = React.useState<string[]>([]);
    const [additionalPrescribedMattersSelection, setAdditionalPrescribedMattersSelection] = React.useState<string[]>([]);
    const [progress, setProgress] = React.useState(0);
    const refresh = useRecoilRefresher_UNSTABLE(allScorecardState);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [redirectToReconsolidation, setRedirectToReconsolidation] = useState(false);
    const setScorecardId = useSetRecoilState(ScorecardAtom);
    const confirmOverrideMessage = 'Please note, if you reconsolidate/update your Consolidated Scorecard, all the data will be deleted and replaced with updated data from the selected source scorecards. Are you sure you want to continue?';
    const [consolidationScorecardId, setConsolidationScorecardId] = useState<string | null>(null);
    const hub = useSignalRConnection("ScorecardCopyHub");

    useEffect(() => {
        if (!consolidationScorecardId) return;

        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                const identifier = `consolidation-${consolidationScorecardId}`;

                hub.on(`taskStarted-${identifier}`, (percent: number) => {
                    setProgress(percent);
                });

                hub.on(`taskProgressChanged-${identifier}`, (percent: number) => {
                    setProgress(percent);
                });

                hub.on(`taskEnded-${identifier}`, (percent: number) => {
                    setProgress(percent);
                });
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, [consolidationScorecardId]);

    useEffect(() => {
        fetchConsolidationRulesMap();
    }, []);

    useEffect(() => {
        if (errors) {
            const elements = Object.keys(errors).map(name => document.getElementsByName(name)[0]).filter(el => !!el);
            elements.sort((a, b) => b.scrollHeight - a.scrollHeight);
            elements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

    }, [errors])

    const handleConfirmation = () => {

        setShowConfirmationDialog(!!props.metaData.scorecardId);

        if(!props.metaData.scorecardId)
        {
            startConsolidation();
        }
    }

    const fetchConsolidationRulesMap = async () => {
        try {

            let scorecaredMapping: { 
                scorecard: ISelectedScorecard; 
                execSelection: EEScorecard.OccupationalTargetDto | undefined;
                otherExecSelection: EEScorecard.OccupationalTargetDto | undefined; 
                seniorSelection: EEScorecard.OccupationalTargetDto | undefined; 
                middleSelection: EEScorecard.OccupationalTargetDto | undefined; 
                juniorSelection: EEScorecard.OccupationalTargetDto | undefined;
             }[] = [];

            if (props.metaData.scorecardId) {
                const result = await EEScorecardService.GetConsolidationRulesMap(props.metaData.scorecardId);
                setConsolidationScorecardId(props.metaData.scorecardId);
                setGeneralInformationSelection(
                    (result.data.generalInformationSource && result.data.generalInformationSource !== emptyGuid) ? result.data.generalInformationSource: "0"
                );
                scorecaredMapping = props.selectedScorecards.map(scorecard => {
                    var execSelectionResult =  result.data.executiveDirectors.find(e => e.sourceScorecardId === scorecard.id);
                    var otherExecSelectionResult =  result.data.otherExecutiveDirectors.find(e => e.sourceScorecardId === scorecard.id);
                    var seniorSelectionResult =  result.data.seniorManagement.find(e => e.sourceScorecardId === scorecard.id);
                    var middleSelectionResult =  result.data.middleManagement.find(e => e.sourceScorecardId === scorecard.id);
                    var juniorSelectionResult =  result.data.juniorManagement.find(e => e.sourceScorecardId === scorecard.id);
                    var employmentEquityPlanSourcesSelectionResult = result.data.employmentEquityPlanSources;

                    setExecSelection({ ...execSelection, [scorecard.id]: execSelectionResult?.occupationalLevel || OccupationalLevelTypes.Executive_Director })
                    setOtherExecSelection({ ...otherExecSelection, [scorecard.id]: otherExecSelectionResult?.occupationalLevel || OccupationalLevelTypes.Other_Executive_Management })
                    setSeniorSelection({ ...seniorSelection, [scorecard.id]:  seniorSelectionResult?.occupationalLevel || OccupationalLevelTypes.Senior_Management });
                    setMiddleSelection({ ...middleSelection, [scorecard.id]:  middleSelectionResult?.occupationalLevel || OccupationalLevelTypes.Middle_Management });
                    setJuniorSelection({ ...juniorSelection, [scorecard.id]:  juniorSelectionResult?.occupationalLevel || OccupationalLevelTypes.Junior_Management });

                    if (!!employmentEquityPlanSourcesSelectionResult?.length) {
                        setEmploymentEquityPlanSources([]); 
                        setObjectivesSelection(employmentEquityPlanSourcesSelectionResult);
                        setBarriersSelection(employmentEquityPlanSourcesSelectionResult);
                        setWorkforceCommentsSelection(employmentEquityPlanSourcesSelectionResult);
                        setWorkforceProjectedHeadcountSelection(employmentEquityPlanSourcesSelectionResult);
                        setWorkforceYearlyTargetsSelection(employmentEquityPlanSourcesSelectionResult);
                        setAdditionalMonitoringProgressSelection(employmentEquityPlanSourcesSelectionResult);
                        setAdditionalDisputeResolutionsSelection(employmentEquityPlanSourcesSelectionResult);
                        setAdditionalSeniorManagersSelection(employmentEquityPlanSourcesSelectionResult);
                        setAdditionalPrescribedMattersSelection(employmentEquityPlanSourcesSelectionResult);
                    } else {
                        setObjectivesSelection(result.data.objectives ?? []);
                        setBarriersSelection(result.data.barriers ?? []);
                        setWorkforceCommentsSelection(result.data.workforceProfileEEA12CommentsPerOccupationalLevel ?? []);
                        setWorkforceProjectedHeadcountSelection(result.data.workforceProfileProjectedHeadcount ?? []);
                        setWorkforceYearlyTargetsSelection(result.data.workforceProfileYearlyTargetsPerOccupationalLevel ?? []);
                        setAdditionalMonitoringProgressSelection(result.data.additionalMonitoringProgress ?? []);
                        setAdditionalDisputeResolutionsSelection(result.data.additionalDisputeResolutions ?? []);
                        setAdditionalSeniorManagersSelection(result.data.additionalSeniorManagers ?? []);
                        setAdditionalPrescribedMattersSelection(result.data.additionalPrescribedMatters ?? []);
                    }
                    
                    return {
                        scorecard,
                        execSelection: execSelectionResult,
                        otherExecSelection: otherExecSelectionResult,
                        seniorSelection: seniorSelectionResult,
                        middleSelection: middleSelectionResult,
                        juniorSelection: juniorSelectionResult
                    }
                });

            } else {
                scorecaredMapping = props.selectedScorecards.map(scorecard => {

                    setExecSelection({ ...execSelection, [scorecard.id]: OccupationalLevelTypes.Executive_Director })
                    setOtherExecSelection({ ...otherExecSelection, [scorecard.id]: OccupationalLevelTypes.Other_Executive_Management })
                    setSeniorSelection({ ...seniorSelection, [scorecard.id]: OccupationalLevelTypes.Senior_Management });
                    setMiddleSelection({ ...middleSelection, [scorecard.id]: OccupationalLevelTypes.Middle_Management });
                    setJuniorSelection({ ...juniorSelection, [scorecard.id]: OccupationalLevelTypes.Junior_Management });

                    return {
                        scorecard,
                        execSelection: undefined,
                        otherExecSelection: undefined,
                        seniorSelection: undefined,
                        middleSelection: undefined,
                        juniorSelection: undefined
                    }
                });
            }
            setSelectedScorecardWithMap(scorecaredMapping);
        } catch (error) {
            console.log(error);
        }
    }

    const startConsolidation = async () => {
        setShowConfirmationDialog(false);
        try {
            setIsLoading(true);
            const ConsolidationScorecardDtoIn: EEScorecard.ConsolidatedScorecardDtoIn = {
                FromDate: props.metaData.startDate,
                ToDate: props.metaData.endDate,
                ScorecardId: props.metaData.scorecardId || emptyGuid,
                ScorecardName: props.metaData.scorecardName,
                ScorecardLicenseId: props.metaData.scorecardLicenseId,
                ScorecardType: 43,
                KeepConsolidatedScorecardUnchanged: (generalInformationSelection === emptyGuid || generalInformationSelection === '0') ? true : false
            }

            const result = await EEScorecardService.CreateNewEEConsolidatedScorecard(ConsolidationScorecardDtoIn);
            if (result.data) {
                setConsolidationScorecardId(result.data);
                const sourceScorecardIds = props.selectedScorecards.map(s => s.id);
                const ConsolidationObject : EEScorecard.ConsolidationDto = {
                    consolidationId: 0,
                    consolidatedScorecardId: result.data,
                    sourceScorecardIds: sourceScorecardIds,

                    executiveDirectors: Object.keys(execSelection).map((key) => {
                        return {
                            sourceScorecardId: key,
                            occupationalLevel: execSelection[key]
                        }
                    }),

                    otherExecutiveDirectors: Object.keys(otherExecSelection).map((key) => {
                        return {
                            sourceScorecardId: key,
                            occupationalLevel: otherExecSelection[key]
                        }
                    }),
                    seniorManagement: Object.keys(seniorSelection).map((key) => {
                        return {
                            sourceScorecardId: key,
                            occupationalLevel: seniorSelection[key]
                        }
                    }),
                    middleManagement: Object.keys(middleSelection).map((key) => {
                        return {
                            sourceScorecardId: key,
                            occupationalLevel: middleSelection[key]
                        }
                    }),
                    juniorManagement: Object.keys(juniorSelection).map((key) => {
                        return {
                            sourceScorecardId: key,
                            occupationalLevel: juniorSelection[key]
                        }
                    }),
                    generalInformationSource: generalInformationSelection == '0' ? emptyGuid : generalInformationSelection,
                    objectives: objectivesSelection.filter(id => id !== emptyGuid),
                    barriers: barriersSelection.filter(id => id !== emptyGuid),
                    workforceProfileEEA12CommentsPerOccupationalLevel: workforceCommentsSelection.filter(id => id !== emptyGuid),
                    workforceProfileProjectedHeadcount: workforceProjectedHeadcountSelection.filter(id => id !== emptyGuid),
                    workforceProfileYearlyTargetsPerOccupationalLevel: workforceYearlyTargetsSelection.filter(id => id !== emptyGuid),
                    additionalMonitoringProgress: additionalMonitoringProgressSelection.filter(id => id !== emptyGuid),
                    additionalDisputeResolutions: additionalDisputeResolutionsSelection.filter(id => id !== emptyGuid),
                    additionalSeniorManagers: additionalSeniorManagersSelection.filter(id => id !== emptyGuid),
                    additionalPrescribedMatters: additionalPrescribedMattersSelection.filter(id => id !== emptyGuid)
                }
                
                const consolidation = await EEScorecardService.ConsolidateScorecard(ConsolidationObject);
                
                if (consolidation.data) {
                    setProgress(100);
                    refresh();
                    setScorecardId({scorecardId: result.data});  

                    if (consolidation.data.wasSuccessful){
                        toast.success('Consolidation completed successfully.');
                        history.push(`/ee/scorecard/${result.data}/general`);
                    }
                    else {
                        toast.warning('Consolidation completed with conflicts. Please resolve them');
                        setRedirectToReconsolidation(true);
                    }               
                }
            }

        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    }

    const employmentEquityPlanOptions = [...props.selectedScorecards.map(s => ({...s, group: 'Select Source Scorecard(s)' })),
        { id: emptyGuid, scorecardName: 'Keep Consolidated Scorecard Unchanged', group: 'Use Consolidated Scorecard' }
    ];

    if (redirectToReconsolidation) {
        return <Reconsolidation/>
    }

    return <>
            <MDTypography variant='h4' color={dark.main} fontWeight='bold'>Consolidation Rules</MDTypography>
            <MDTypography color={dark.main} fontSize='1rem'>Configure what data must be populated from which scorecards.</MDTypography>
 
            <MDTypography variant='h5' color={dark.main} fontWeight='bold' paddingTop='20px'>General Information</MDTypography>
                   <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>General Information</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Select
                                    style={{ height: 40, padding: 1 }}
                                    labelId="demo-simple-select-label"
                                    value={generalInformationSelection}
                                    onChange={(e) => {
                                        setGeneralInformationSelection(e.target.value)
                                    }}
                                >
                                    <ListSubheader sx={{color: dark.main}}>Use Consolidated Scorecard</ListSubheader>
                                    <MenuItem value={emptyGuid} sx={{ pl: 3 }}>Keep Consolidated Scorecard Unchanged</MenuItem>

                                    <ListSubheader sx={{color: dark.main}}>Select Source Scorecard</ListSubheader>
                                    {generalInformation.map((item) => {
                                        return <MenuItem value={item.id} sx={{ pl: 3 }}>{item.scorecardName}</MenuItem>
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

            <MDTypography variant='h5' color={dark.main} fontWeight='bold'>Employee Structure</MDTypography>
            <MDTypography color={dark.main} fontSize='1rem'>Employees from the selected source scorecards will be copied to the consolidated scorecard. 
                The section below allows you to change the occupational levels per scorecard if required. Please select any adjustments that should be made:
            </MDTypography>
                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Executive Directors </MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            {selectedScorecardWithMap.map(({scorecard, execSelection}: {scorecard: ISelectedScorecard, execSelection: EEScorecard.OccupationalTargetDto|undefined})=> {
                                return <FormControl fullWidth sx={{ paddingBottom: '15px' }}>
                                    <InputLabel id="demo-simple-select-label">{scorecard.scorecardName}</InputLabel>
                                    <Select
                                        style={{ height: 40, padding: 1 }}
                                        labelId="demo-simple-select-label"
                                        defaultValue={execSelection?.occupationalLevel || OccupationalLevelTypes.Executive_Director}
                                        onChange={(e) => {
                                            setExecSelection({ ...execSelection, [scorecard.id]: e.target.value })
                                        }}
                                    >
                                        <MenuItem value={OccupationalLevelTypes.Executive_Director}>Executive Director</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Other_Executive_Management}>Other Executive Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Senior_Management}>Senior Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Unspecified}>Discard</MenuItem>
                                    </Select>
                                </FormControl>
                            })}
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Other Executive Management </MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            {selectedScorecardWithMap.map(({scorecard, otherExecSelection}: {scorecard: ISelectedScorecard, otherExecSelection: EEScorecard.OccupationalTargetDto|undefined}) => {
                                return <FormControl fullWidth sx={{ paddingBottom: '15px' }}>
                                    <InputLabel id="demo-simple-select-label">{scorecard.scorecardName}</InputLabel>
                                    <Select
                                        style={{ height: 40, padding: 1 }}
                                        labelId="demo-simple-select-label"
                                        defaultValue={otherExecSelection?.occupationalLevel || OccupationalLevelTypes.Other_Executive_Management}
                                        onChange={(e) => {
                                            setOtherExecSelection({ ...otherExecSelection, [scorecard.id]: e.target.value })
                                        }}

                                    >
                                        <MenuItem value={OccupationalLevelTypes.Other_Executive_Management}>Other Executive Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Senior_Management}>Senior Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Middle_Management}>Middle Management</MenuItem>
                                    </Select>
                                </FormControl>
                            })}
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Senior Management </MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            {selectedScorecardWithMap.map(({scorecard, seniorSelection}: {scorecard: ISelectedScorecard, seniorSelection: EEScorecard.OccupationalTargetDto|undefined}) => {
                                return <FormControl fullWidth sx={{ paddingBottom: '15px' }}>
                                    <InputLabel id="demo-simple-select-label">{scorecard.scorecardName}</InputLabel>
                                    <Select
                                        style={{ height: 40, padding: 1 }}
                                        labelId="demo-simple-select-label"
                                        defaultValue={seniorSelection?.occupationalLevel || OccupationalLevelTypes.Senior_Management}
                                        onChange={(e) => {
                                            setSeniorSelection({ ...seniorSelection, [scorecard.id]: e.target.value })
                                        }}
                                    >
                                        <MenuItem value={OccupationalLevelTypes.Other_Executive_Management}>Other Executive Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Senior_Management}>Senior Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Middle_Management}>Middle Management</MenuItem>
                                    </Select>
                                </FormControl>
                            })}
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Middle Management </MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            {selectedScorecardWithMap.map(({scorecard, middleSelection}: {scorecard: ISelectedScorecard, middleSelection: EEScorecard.OccupationalTargetDto|undefined}) => {
                                return <FormControl fullWidth sx={{ paddingBottom: '15px' }}>
                                    <InputLabel id="demo-simple-select-label">{scorecard.scorecardName}</InputLabel>
                                    <Select
                                        style={{ height: 40, padding: 1 }}
                                        labelId="demo-simple-select-label"
                                        defaultValue={middleSelection?.occupationalLevel || OccupationalLevelTypes.Middle_Management}
                                        onChange={(e) => {
                                            setMiddleSelection({ ...middleSelection, [scorecard.id]: e.target.value })
                                        }}
                                    >
                                        <MenuItem value={OccupationalLevelTypes.Senior_Management}>Senior Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Middle_Management}>Middle Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Junior_Management}>Junior Management</MenuItem>
                                    </Select>
                                </FormControl>
                            })}
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Junior Management </MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            {selectedScorecardWithMap.map(({scorecard, juniorSelection}: {scorecard: ISelectedScorecard, juniorSelection: EEScorecard.OccupationalTargetDto|undefined}) => {
                                return <FormControl fullWidth sx={{ paddingBottom: '15px' }}>
                                    <InputLabel id="demo-simple-select-label">{scorecard.scorecardName}</InputLabel>
                                    <Select
                                        style={{ height: 40, padding: 1 }}
                                        labelId="demo-simple-select-label"
                                        defaultValue={juniorSelection?.occupationalLevel || OccupationalLevelTypes.Junior_Management}
                                        onChange={(e) => {
                                            setJuniorSelection({ ...juniorSelection, [scorecard.id]: e.target.value })
                                        }}
                                    >
                                        <MenuItem value={OccupationalLevelTypes.Junior_Management}>Junior Management</MenuItem>
                                        <MenuItem value={OccupationalLevelTypes.Semi_Skilled}>Semi-Skilled</MenuItem>
                                    </Select>
                                </FormControl>
                            })}
                        </Box>
                    </Grid>
                </Grid>

            <MDTypography variant='h5' color={dark.main} fontWeight='bold'>EE Plan</MDTypography>
              <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Objectives</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => objectivesSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = objectivesSelection.includes(emptyGuid); 
                                        return (objectivesSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setObjectivesSelection([emptyGuid]);
                                        } else {
                                            setObjectivesSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                  <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Barriers</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => barriersSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = barriersSelection.includes(emptyGuid); 
                                        return (barriersSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setBarriersSelection([emptyGuid]);
                                        } else {
                                            setBarriersSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Workforce Profile - Projected Headcounts</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => workforceProjectedHeadcountSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = workforceProjectedHeadcountSelection.includes(emptyGuid); 
                                        return (workforceProjectedHeadcountSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setWorkforceProjectedHeadcountSelection([emptyGuid]);
                                        } else {
                                            setWorkforceProjectedHeadcountSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Workforce Profile - EEA12 Comments per Occupational Level</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => workforceCommentsSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = workforceCommentsSelection.includes(emptyGuid); 
                                        return (workforceCommentsSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setWorkforceCommentsSelection([emptyGuid]);
                                        } else {
                                            setWorkforceCommentsSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                 <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Workforce Profile - Yearly Targets per Occupational Level</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                  <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => workforceYearlyTargetsSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = workforceYearlyTargetsSelection.includes(emptyGuid); 
                                        return (workforceYearlyTargetsSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setWorkforceYearlyTargetsSelection([emptyGuid]);
                                        } else {
                                            setWorkforceYearlyTargetsSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Additional - Monitoring Progress</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => additionalMonitoringProgressSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = additionalMonitoringProgressSelection.includes(emptyGuid); 
                                        return (additionalMonitoringProgressSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setAdditionalMonitoringProgressSelection([emptyGuid]);
                                        } else {
                                            setAdditionalMonitoringProgressSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Additional - Dispute Resolutions</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => additionalDisputeResolutionsSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = additionalDisputeResolutionsSelection.includes(emptyGuid); 
                                        return (additionalDisputeResolutionsSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setAdditionalDisputeResolutionsSelection([emptyGuid]);
                                        } else {
                                            setAdditionalDisputeResolutionsSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Additional - Senior Managers</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                 <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => additionalSeniorManagersSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = additionalSeniorManagersSelection.includes(emptyGuid); 
                                        return (additionalSeniorManagersSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setAdditionalSeniorManagersSelection([emptyGuid]);
                                        } else {
                                            setAdditionalSeniorManagersSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container padding={2}>
                    <Grid item xs={3} xl={3}>
                        <MDTypography variant='h6' color={dark.main}>Additional - Prescribed Matters</MDTypography>
                    </Grid>
                    <Grid item xs={6} xl={6}>
                        <Box>
                            <FormControl fullWidth sx={{ paddingBottom: '10px' }}>
                                <Autocomplete
                                    multiple
                                    size='small'
                                    forcePopupIcon={false}
                                    style={{ height: 40, padding: 1 }}
                                    groupBy={(option) => option.group || ''}
                                    disableCloseOnSelect={true}
                                    value={employmentEquityPlanOptions.filter((option) => additionalPrescribedMattersSelection.includes(option.id))}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                    getOptionLabel={(option: any) => option.scorecardName}
                                    getOptionDisabled={(option: any) => {
                                        const isConsolidatedScorecard = additionalPrescribedMattersSelection.includes(emptyGuid); 
                                        return (additionalPrescribedMattersSelection.length > 0) && (( isConsolidatedScorecard && option.group === 'Select Source Scorecard(s)') || 
                                        (!isConsolidatedScorecard && option.group === 'Use Consolidated Scorecard'));
                                    }}
                                    options={[...employmentEquityPlanOptions].sort((a, b) => b.group?1:-1 - (a.group?1:-1))}
                                    onChange={(event: any, newValues: any) => {
                                        const consolidated = newValues.find((o: any) => o.group === 'Use Consolidated Scorecard');
                                        if (consolidated) {
                                            setAdditionalPrescribedMattersSelection([emptyGuid]);
                                        } else {
                                            setAdditionalPrescribedMattersSelection(newValues.filter((o: any) => o.group !== 'Use Consolidated Scorecard')
                                                .map((source: ISelectedScorecard) => source.id) );
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                        {...params}
                                        variant="outlined"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>


            <MDTypography variant='h5' color={dark.main} fontWeight='bold'>Not Consolidated</MDTypography>
                <MDTypography variant='body2' color={dark.main} fontWeight='regular' style={{paddingTop: '10px'}}>The following data is not consolidated 
                    and must be captured directly on your consolidated scorecard.
                </MDTypography>
            
            <MDTypography variant='h6' color={dark.main} fontWeight='bold' style={{paddingTop: '10px'}}>This data will not be overwritten during consolidation:</MDTypography>
            <ul style={{paddingLeft: '50px'}}>
                <li><MDTypography variant='body2' color={dark.main} fontWeight='regular'>Workforce Profile - Deviations per demographic per year</MDTypography></li>
                <li><MDTypography variant='body2' color={dark.main} fontWeight='regular'>Workforce Profile - Deviations per occupational level per year</MDTypography></li>
                <li><MDTypography variant='body2' color={dark.main} fontWeight='regular'>Workforce Profile - Monitoring and Evaluations</MDTypography></li>
            </ul>

            <MDTypography variant='h6' color={dark.main} fontWeight='bold'>This data will be overwritten during consolidation and must be recaptured:</MDTypography>
            <ul style={{paddingLeft: '50px'}}>
                <li><MDTypography variant='body2' color={dark.main} fontWeight='regular'>EE Committee - Members</MDTypography></li>
                <li><MDTypography variant='body2' color={dark.main} fontWeight='regular'>EE Committee - Minutes</MDTypography></li>
            </ul>

            <Grid container spacing={2} pt={2}>
                <Grid item xs={10} justifyContent={'flex-start'}>
                    <MDButton variant="gradient" color="secondary" onClick={() => { props.previous() }}>Previous</MDButton>
                </Grid>
                <Grid item xs={2} justifyContent={'flex-end'}>
                    <LoadingButton variant='contained' loading={isLoading} color="primary" onClick={handleConfirmation} loadingPosition='end'>
                        Start Consolidation
                    </LoadingButton>
                    <LinearProgressWithLabel value={progress} />
                </Grid>
            </Grid>

        <ConfirmationDialog
            showDialog={showConfirmationDialog}
            handleAccept={startConsolidation}
            handleCancel={() => setShowConfirmationDialog(false)}
            heading={"Confirm Update"}
            message={confirmOverrideMessage}
        />
    </>

}


